/**
 * ComplyV's legal pages: terms, privacy, refunds, contact.
 *
 * Public on purpose — no AppLayout, no login. A payment gateway's reviewer
 * opens these logged out, and so does a customer deciding whether to trust us.
 *
 * All four render from src/new-ui/legal/policies.js; this file is layout only.
 * If a fact in COMPANY is still a [[PLACEHOLDER]], the page says so in amber at
 * the top and lists which ones — a half-filled legal page should be impossible
 * to publish by accident.
 */

import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { POLICIES, POLICY_LINKS, COMPANY, unfilledFacts, isPlaceholder } from '../legal/policies';
import { ROUTES } from '../constants/constants';

const INK = '#08111f';
const MUTED = '#7a7060';
const BORDER = '#ddd6c6';
const GOLD = '#8a6a1f';

/** Mark a placeholder inside a sentence so it cannot be skim-read past. */
const renderText = (text) => {
  const parts = String(text).split(/(\[\[[^\]]+\]\])/g);
  return parts.map((part, i) => (isPlaceholder(part)
    ? (
      <mark key={i} style={{ background: '#f3ebd7', color: GOLD, padding: '0 3px', borderRadius: 3 }}>
        {part}
      </mark>
    )
    : <span key={i}>{part}</span>));
};

export default function LegalPage({ policyId }) {
  const params = useParams();
  const id = policyId || params.policyId;
  const policy = POLICIES[id];

  useEffect(() => {
    if (policy) document.title = `${policy.title} · ${COMPANY.product}`;
  }, [policy]);

  if (!policy) return <Navigate to="/legal/terms" replace />;

  const missing = unfilledFacts();

  return (
    <div className="min-h-screen" style={{ background: '#f4f0e8' }}>
      <header className="px-6 py-4" style={{ borderBottom: `1px solid ${BORDER}`, background: '#fffdf8' }}>
        <div className="max-w-[820px] mx-auto flex items-center justify-between gap-4 flex-wrap">
          <a href={COMPANY.website} className="text-[15px] font-bold" style={{ color: INK }}>
            {COMPANY.product}
          </a>
          <nav className="flex gap-4 flex-wrap">
            {POLICY_LINKS.map((link) => (
              <Link
                key={link.id}
                to={`/legal/${link.id}`}
                className="text-[12.5px] hover:underline"
                style={{ color: link.id === id ? GOLD : MUTED, fontWeight: link.id === id ? 600 : 400 }}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-[820px] mx-auto px-6 py-10">
        {missing.length > 0 && (
          <div
            className="mb-8 px-4 py-3 rounded-lg text-[12.5px] leading-relaxed"
            style={{ background: '#f3ebd7', color: GOLD, border: `1px solid #eadfc0` }}
          >
            <strong>Draft — not yet final.</strong> {missing.length} detail{missing.length === 1 ? '' : 's'} still to be
            filled in{': '}{missing.join(', ')}. These pages also need a lawyer&apos;s review before launch.
          </div>
        )}

        <h1 className="text-[28px] font-bold mb-2" style={{ color: INK }}>{policy.title}</h1>
        <p className="text-[12px] mb-6" style={{ color: MUTED }}>
          Effective {renderText(COMPANY.effectiveDate)}
        </p>

        <p className="text-[14px] leading-7 mb-8" style={{ color: INK }}>{renderText(policy.intro)}</p>

        {policy.sections.map((section) => (
          <section key={section.heading} className="mb-7">
            <h2 className="text-[16px] font-semibold mb-2.5" style={{ color: INK }}>{section.heading}</h2>
            {section.paragraphs.map((para, i) => (
              <p key={i} className="text-[13.5px] leading-7 mb-2.5" style={{ color: '#3a3a3a' }}>
                {renderText(para)}
              </p>
            ))}
          </section>
        ))}

        <footer className="mt-12 pt-6 text-[12px]" style={{ borderTop: `1px solid ${BORDER}`, color: MUTED }}>
          <div className="flex gap-4 flex-wrap mb-3">
            {POLICY_LINKS.filter((l) => l.id !== id).map((link) => (
              <Link key={link.id} to={`/legal/${link.id}`} className="hover:underline" style={{ color: MUTED }}>
                {link.label}
              </Link>
            ))}
            <Link to={ROUTES.LOGIN} className="hover:underline" style={{ color: MUTED }}>Sign in</Link>
          </div>
          <div>{renderText(COMPANY.entity)} · {renderText(COMPANY.address)}</div>
        </footer>
      </main>
    </div>
  );
}
